import {
  ArrowLeft,
  BriefcaseBusiness,
  CheckCircle2,
  LoaderCircle,
  MapPin,
  Send,
  Sparkles,
  Upload,
} from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
type Job = {
  reference: string;
  title: string;
  description: string;
  requirements?: string | null;
  location?: string | null;
  closesAt: string;
  screeningCriteria?: {
    requiredKeywords?: string[];
    preferredKeywords?: string[];
  };
};
class ApiError extends Error {
  constructor(message: string, public status: number) { super(message); }
}
async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api/v1${path}`, init);
  const body = await response
    .json()
    .catch(() => ({ message: response.status === 413 ? "Le CV est trop volumineux pour le serveur d’envoi." : "Réponse serveur invalide" }));
  if (!response.ok)
    throw new ApiError((body as { message?: string }).message ?? "Opération impossible", response.status);
  return body as T;
}
export function PublicCareersPage() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selected, setSelected] = useState<Job>();
  const [cv, setCv] = useState<File>();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState<{
    applicationId: string;
    status: string;
    score?: number;
    recommendation?: string;
    summary?: string;
  }>();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    location: "",
    yearsExperience: "",
    highestDegree: "",
    skills: "",
    coverMessage: "",
    linkedinUrl: "",
    consent: false,
  });
  useEffect(() => {
    void api<Job[]>("/public/jobs")
      .then(setJobs)
      .catch((reason) => setError(reason.message));
  }, []);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected || !cv)
      return setError("Choisissez une offre et joignez votre CV.");
    if (cv.size > 25 * 1024 * 1024)
      return setError("Le CV ne doit pas dépasser 25 Mo.");
    setBusy(true);
    setError("");
    setProgress("Enregistrement de votre candidature…");
    try {
      const created = await api<{ applicationId: string; status: string; cvUploadUrl?: string; alreadySubmitted?: boolean }>(
        "/public/applications",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...form,
            jobReference: selected.reference,
            yearsExperience: Number(form.yearsExperience || 0),
            consent: true,
          }),
        },
      );
      if (created.alreadySubmitted) {
        setResult({ applicationId: created.applicationId, status: created.status });
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (!created.cvUploadUrl) throw new Error("Le lien sécurisé d’envoi du CV est indisponible.");
      setProgress("Envoi et vérification de votre CV…");
      const body = new FormData();
      body.append("file", cv);
      const screened = await api<NonNullable<typeof result>>(
        created.cvUploadUrl.replace("/api/v1", ""),
        { method: "POST", body },
      );
      setResult(screened);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (reason) {
      setError(reason instanceof ApiError && reason.status === 413
        ? "Votre candidature est conservée, mais le CV n’a pas pu être transmis car le frontal limite encore la taille des fichiers. Réduisez temporairement le CV à moins de 1 Mo puis cliquez de nouveau sur Envoyer."
        : reason instanceof Error ? reason.message : "Candidature impossible.");
    } finally {
      setBusy(false);
      setProgress("");
    }
  };
  return (
    <main className="careers-page">
      <header className="careers-header">
        <Link to="/">
          <ArrowLeft size={17} /> Connexion collaborateurs
        </Link>
        <img src="/sanru-logo.jpg" alt="SANRU" />
        <div>
          <strong>Carrières SANRU</strong>
          <small>Santé pour tous et par tous</small>
        </div>
      </header>
      <section className="careers-hero">
        <span>
          <Sparkles size={16} /> Rejoignez notre mission
        </span>
        <h1>Construisons ensemble une santé durable en RDC.</h1>
        <p>
          Consultez nos opportunités et transmettez votre candidature en toute
          sécurité, sans créer de compte.
        </p>
      </section>
      {result ? (
        <section className="application-result">
          <CheckCircle2 size={42} />
          <span>✓ Candidature enregistrée</span>
          <h2>Félicitations !</h2>
          <p>Votre candidature a été soumise et enregistrée. L’équipe RH SANRU vous contactera pour la suite.</p>
          <aside className="application-confirmation"><CheckCircle2 size={18}/> Un message de confirmation a été préparé pour votre boîte e-mail.</aside>
          <button
            className="button button--primary"
            onClick={() => {
              setResult(undefined);
              setSelected(undefined);
            }}
          >
            Voir d’autres offres
          </button>
        </section>
      ) : (
        <section className="careers-workspace">
          <aside className="public-jobs">
            <h2>
              Offres ouvertes <span>{jobs.length}</span>
            </h2>
            {jobs.length ? (
              jobs.map((job) => (
                <button
                  className={
                    selected?.reference === job.reference ? "active" : ""
                  }
                  key={job.reference}
                  onClick={() => setSelected(job)}
                >
                  <span>
                    <BriefcaseBusiness size={18} />
                  </span>
                  <div>
                    <strong>{job.title}</strong>
                    <small>
                      <MapPin size={13} />
                      {job.location || "RDC"} · clôture{" "}
                      {new Date(job.closesAt).toLocaleDateString("fr-CD")}
                    </small>
                  </div>
                </button>
              ))
            ) : (
              <p>Aucune offre ouverte actuellement.</p>
            )}
          </aside>
          <article className="public-job-detail">
            {!selected ? (
              <div className="public-empty">
                <BriefcaseBusiness size={40} />
                <h2>Sélectionnez une offre</h2>
                <p>
                  Les détails et le formulaire de candidature apparaîtront ici.
                </p>
              </div>
            ) : (
              <>
                <div className="job-public-heading">
                  <span>{selected.reference}</span>
                  <h2>{selected.title}</h2>
                  <p>
                    <MapPin size={15} />
                    {selected.location || "RDC"}
                  </p>
                </div>
                <p className="job-description">{selected.description}</p>
                {selected.requirements && (
                  <section>
                    <h3>Profil recherché</h3>
                    <p>{selected.requirements}</p>
                  </section>
                )}
                <form className="public-application-form" onSubmit={submit}>
                  <h3>Déposer ma candidature</h3>
                  <div className="form-grid">
                    <label>
                      Prénom
                      <input
                        value={form.firstName}
                        onChange={(e) =>
                          setForm({ ...form, firstName: e.target.value })
                        }
                        required
                      />
                    </label>
                    <label>
                      Nom
                      <input
                        value={form.lastName}
                        onChange={(e) =>
                          setForm({ ...form, lastName: e.target.value })
                        }
                        required
                      />
                    </label>
                    <label>
                      E-mail
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) =>
                          setForm({ ...form, email: e.target.value })
                        }
                        required
                      />
                    </label>
                    <label>
                      Téléphone
                      <input
                        value={form.phone}
                        onChange={(e) =>
                          setForm({ ...form, phone: e.target.value })
                        }
                      />
                    </label>
                    <label>
                      Localisation
                      <input
                        value={form.location}
                        onChange={(e) =>
                          setForm({ ...form, location: e.target.value })
                        }
                      />
                    </label>
                    <label>
                      Années d’expérience
                      <input
                        type="number"
                        min="0"
                        value={form.yearsExperience}
                        onChange={(e) =>
                          setForm({ ...form, yearsExperience: e.target.value })
                        }
                      />
                    </label>
                    <label>
                      Diplôme le plus élevé
                      <input
                        value={form.highestDegree}
                        onChange={(e) =>
                          setForm({ ...form, highestDegree: e.target.value })
                        }
                      />
                    </label>
                    <label>
                      Compétences principales
                      <input
                        value={form.skills}
                        onChange={(e) =>
                          setForm({ ...form, skills: e.target.value })
                        }
                        placeholder="Suivi-évaluation, Excel…"
                      />
                    </label>
                    <label className="form-grid__wide">Profil LinkedIn<input type="url" value={form.linkedinUrl} onChange={(e)=>setForm({...form,linkedinUrl:e.target.value})} placeholder="https://www.linkedin.com/in/votre-profil"/></label>
                    <label className="form-grid__wide">
                      Message de motivation
                      <textarea
                        value={form.coverMessage}
                        onChange={(e) =>
                          setForm({ ...form, coverMessage: e.target.value })
                        }
                      />
                    </label>
                  </div>
                  <label className="public-cv-upload">
                    <Upload size={22} />
                    <span>{cv?.name ?? "Joindre mon CV en PDF ou DOCX"}</span>
                    <input
                      type="file"
                      accept=".pdf,.docx"
                      onChange={(e) => setCv(e.target.files?.[0])}
                      required
                    />
                  </label>
                  <label className="consent-field">
                    <input
                      type="checkbox"
                      checked={form.consent}
                      onChange={(e) =>
                        setForm({ ...form, consent: e.target.checked })
                      }
                      required
                    />
                    J’autorise SANRU à traiter ces données pour ce recrutement.
                  </label>
                  {error && <p className="form-error">{error}</p>}
                  {progress && <p className="form-progress"><LoaderCircle className="spin" size={17}/>{progress}</p>}
                  <button
                    className="button button--primary public-submit"
                    disabled={busy || !form.consent}
                  >
                    {busy ? (
                      <LoaderCircle className="spin" size={18} />
                    ) : (
                      <Send size={18} />
                    )}
                    Envoyer et analyser mon dossier
                  </button>
                </form>
              </>
            )}
          </article>
        </section>
      )}
    </main>
  );
}
